import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface Role {
    role_id: number;
    role_name: string;
    description: string | null;
    category: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export type RolePayload = Omit<Role, "role_id" | "created_at" | "updated_at">;

// GET All Roles
export const useAdminRoles = () => {
    return useQuery<Role[]>({
        queryKey: ["admin-roles"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/roles", { withCredentials: true });
            return data;
        },
    });
};

// GET Single Role
export const useAdminRole = (id: number | null) => {
    return useQuery<Role>({
        queryKey: ["admin-role", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/roles/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Role
export const useCreateRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: RolePayload) => {
            const { data } = await axiosClient.post("/roles", payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
        },
    });
};

// UPDATE Role
export const useUpdateRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<RolePayload> }) => {
            const { data } = await axiosClient.put(`/roles/${id}`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
        },
    });
};

// DELETE Role
export const useDeleteRole = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/roles/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
        },
    });
};
